import React, { useState, useEffect } from 'react';
import { Building2, Users, Car, DollarSign, ArrowRight, AlertTriangle } from 'lucide-react';
import StatsCard from '../common/StatsCard';
import StatusBadge from '../common/StatusBadge';
import { AdminDashboardAPI, APIUtils } from '../../services/api';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
};

const expiryLabel = (dateStr) => {
  const d = daysUntil(dateStr);
  if (d == null) return null;
  if (d < 0) return { text: 'Expired', color: 'text-red-600' };
  if (d <= 7) return { text: `${d}d left`, color: 'text-red-600' };
  return { text: `${d}d left`, color: 'text-amber-600' };
};

const AdminDashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await AdminDashboardAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Dashboard error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of pending approvals and system status</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Pending Companies"
          value={stats?.pendingCompanies || 0}
          icon={Building2}
          color="yellow"
          subtitle={`${stats?.totalCompanies || 0} total`}
          onClick={() => onNavigate('companies')}
        />
        <StatsCard
          title="Pending Drivers"
          value={stats?.pendingDrivers || 0}
          icon={Users}
          color="emerald"
          subtitle={`${stats?.totalDrivers || 0} total`}
          onClick={() => onNavigate('drivers')}
        />
        <StatsCard
          title="Pending Vehicles"
          value={stats?.pendingVehicles || 0}
          icon={Car}
          color="emerald"
          subtitle={`${stats?.totalVehicles || 0} total`}
          onClick={() => onNavigate('vehicles')}
        />
        <StatsCard
          title="Pending Payouts"
          value={stats?.pendingPayouts || 0}
          icon={DollarSign}
          color="green"
          subtitle={stats?.pendingPayoutAmount ? `R${(stats.pendingPayoutAmount / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'R0,00'}
          onClick={() => onNavigate('payouts')}
        />
      </div>

      {/* Revenue & amounts owed — aggregated from settled ride transactions
          (card + cash) so the admin sees platform revenue and what still needs
          to be paid out to drivers and companies. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Revenue"
          value={`R${((stats?.revenue?.gross || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={DollarSign}
          color="green"
          subtitle={`${stats?.revenue?.rideCount || 0} rides · R${((stats?.revenue?.zuttleCommission || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} commission`}
        />
        <StatsCard
          title="Owed to Drivers"
          value={`R${((stats?.owedToDrivers || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={Users}
          color="yellow"
          subtitle="Unpaid driver earnings"
          onClick={() => onNavigate('payouts')}
        />
        <StatsCard
          title="Owed to Companies"
          value={`R${((stats?.owedToCompanies || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={Building2}
          color="yellow"
          subtitle="Unpaid company earnings"
          onClick={() => onNavigate('payouts')}
        />
        <StatsCard
          title="Total to Pay Out"
          value={`R${((stats?.totalOwed || 0) / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={DollarSign}
          color="green"
          subtitle="Drivers + companies"
          onClick={() => onNavigate('payouts')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Pending Companies */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Company Registrations</h2>
            <button onClick={() => onNavigate('companies')} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center">
              View all <ArrowRight size={14} className="ml-1" />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recentPendingCompanies?.length > 0 ? (
              stats.recentPendingCompanies.map((company) => (
                <div key={company._id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{company.companyName}</p>
                    <p className="text-sm text-gray-500">{company.email}</p>
                  </div>
                  <StatusBadge status={company.registrationStatus} />
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">No pending companies</div>
            )}
          </div>
        </div>

        {/* Recent Pending Drivers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Driver Registrations</h2>
            <button onClick={() => onNavigate('drivers')} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center">
              View all <ArrowRight size={14} className="ml-1" />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recentPendingDrivers?.length > 0 ? (
              stats.recentPendingDrivers.map((driver) => (
                <div key={driver._id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{driver.name} {driver.surname}</p>
                    <p className="text-sm text-gray-500">{driver.company?.companyName || 'Unknown company'}</p>
                  </div>
                  <StatusBadge status={driver.approvalStatus} />
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">No pending drivers</div>
            )}
          </div>
        </div>
      </div>

      {/* Expiry Alerts */}
      {stats?.expiringDrivers?.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-amber-200">
          <div className="p-4 border-b border-amber-100 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Expiry Alerts</h2>
            <span className="ml-auto text-xs text-gray-500">Licenses &amp; insurance expiring within 30 days</span>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.expiringDrivers.map((driver) => {
              const lic = expiryLabel(driver.licenseExpiry);
              const ins = expiryLabel(driver.insuranceExpiry);
              return (
                <div key={driver._id} className="p-4 flex items-center justify-between hover:bg-amber-50">
                  <div>
                    <p className="font-medium text-gray-900">{driver.name} {driver.surname}</p>
                    <p className="text-sm text-gray-500">{driver.company?.companyName}</p>
                  </div>
                  <div className="flex gap-3 text-xs">
                    {lic && (
                      <span className={`font-medium ${lic.color}`}>License: {lic.text}</span>
                    )}
                    {ins && (
                      <span className={`font-medium ${ins.color}`}>Insurance: {ins.text}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
